import { Link } from "react-router-dom";
import { ExternalLink, Check, X, ArrowRight, Scale, Trash2 } from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import SelloBadge from "@/components/SelloBadge";

const ComparePage = () => {
  const { items, removeCompare, clearCompare } = useCompare();
  const allTags = [...new Set(items.flatMap((i) => i.tags || []))];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-12">
        <div className="flex items-center justify-between gap-4">
          <div>
            <span className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400">Decide mejor</span>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2">
              <Scale className="h-7 w-7 text-emerald-400" /> Comparador
            </h1>
            <p className="mt-2 text-muted-foreground">Marca productos con el ícono <Scale className="inline h-3.5 w-3.5" /> en el catálogo o tus favoritos para verlos aquí lado a lado.</p>
          </div>
          {items.length > 0 && (
            <Button data-testid="compare-clear-btn" variant="outline" onClick={clearCompare} className="rounded-full shrink-0">
              <Trash2 className="h-4 w-4 mr-1" /> Vaciar
            </Button>
          )}
        </div>

        {items.length === 0 ? (
          <div data-testid="compare-empty" className="text-center py-24 rounded-3xl border border-dashed border-border mt-8">
            <Scale className="mx-auto h-12 w-12 text-muted-foreground/40" />
            <p className="mt-4 text-muted-foreground">Aún no has añadido productos al comparador.</p>
            <Link to="/catalogo"><Button className="mt-6 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-bold">Explorar catálogo <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
        ) : items.length < 2 ? (
          <div className="mt-10 text-center rounded-3xl border border-dashed border-border py-16">
            <p className="text-muted-foreground">Añade al menos 2 productos para comparar.</p>
            <Link to="/catalogo"><Button variant="outline" className="mt-4 rounded-full">Ir al catálogo</Button></Link>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto">
            <table data-testid="compare-table" className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="w-40 p-3"></th>
                  {items.map((p) => (
                    <th key={p.id} className="p-3 min-w-[220px] align-top">
                      <div className="relative rounded-2xl border border-border bg-card overflow-hidden">
                        <button
                          data-testid={`compare-remove-${p.id}`}
                          onClick={() => removeCompare(p.id)}
                          className="absolute top-2 right-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-slate-950/70 glass text-white hover:bg-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <div className="relative aspect-square bg-secondary">
                          <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                          <div className="absolute top-2 left-2"><SelloBadge /></div>
                        </div>
                        <div className="p-3 text-left">
                          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-emerald-500/80">{p.brand}</div>
                          <Link to={`/producto/${p.id}`} className="font-display font-semibold text-sm leading-snug line-clamp-2 hover:text-emerald-400">{p.name}</Link>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-t border-border">
                  <td className="p-3 font-mono-tech text-xs uppercase tracking-widest text-muted-foreground">Precio</td>
                  {items.map((p) => <td key={p.id} className="p-3 text-center font-display font-bold">{p.price != null ? `$${p.price.toFixed(2)}` : "—"}</td>)}
                </tr>
                <tr className="border-t border-border bg-secondary/20">
                  <td className="p-3 font-mono-tech text-xs uppercase tracking-widest text-muted-foreground">Pureza</td>
                  {items.map((p) => <td key={p.id} className="p-3 text-center font-bold text-emerald-400">{p.purity_score}%</td>)}
                </tr>
                {allTags.map((tag) => (
                  <tr key={tag} className="border-t border-border">
                    <td className="p-3 text-xs text-muted-foreground">{tag}</td>
                    {items.map((p) => (
                      <td key={p.id} className="p-3 text-center">
                        {(p.tags || []).includes(tag)
                          ? <Check className="mx-auto h-4 w-4 text-emerald-400" />
                          : <X className="mx-auto h-4 w-4 text-muted-foreground/30" />}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t border-border">
                  <td className="p-3"></td>
                  {items.map((p) => (
                    <td key={p.id} className="p-3 text-center">
                      <a
                        data-testid={`compare-buy-${p.id}`}
                        href={`/recomienda/${p.category}/${p.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 px-3 py-2 text-xs font-bold text-slate-950"
                      >
                        Comprar <ExternalLink className="h-3 w-3" />
                      </a>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ComparePage;
